import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EmptyState, PageHeader } from "@pericles/framework-frontend";

import {
  fetchConversation,
  promoteConversationMessageToSpec,
  sendConversationMessage,
} from "../api/projectConversationClient";
import { fetchSpecSections } from "../api/specsClient";
import type { ProjectConversation, ProjectConversationMessage } from "../types/projectConversations";
import type { SpecSection } from "../types/specs";

import { InlineAlert } from "./InlineAlert";

interface ProjectConversationPanelProps {
  projectId: string;
  onSpecUpdated?: () => void;
  onMessagesChange?: (messages: ProjectConversationMessage[]) => void;
}

export function ProjectConversationPanel({
  projectId,
  onSpecUpdated,
  onMessagesChange,
}: ProjectConversationPanelProps): JSX.Element {
  const [conversation, setConversation] = useState<ProjectConversation | null>(null);
  const [messages, setMessages] = useState<ProjectConversationMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [promotionNotice, setPromotionNotice] = useState<string | null>(null);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [specSections, setSpecSections] = useState<SpecSection[]>([]);
  const [isSectionPickerOpen, setIsSectionPickerOpen] = useState<boolean>(false);
  const [selectedSpecSectionId, setSelectedSpecSectionId] = useState<string>("new");
  const [isLoadingSpecSections, setIsLoadingSpecSections] = useState<boolean>(false);
  const [sectionPickerError, setSectionPickerError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [messageToPromote, setMessageToPromote] = useState<ProjectConversationMessage | null>(null);
  const [newSectionCategory, setNewSectionCategory] = useState<string>("");
  const [newSectionKind, setNewSectionKind] = useState<string>("");
  const [newSectionTitle, setNewSectionTitle] = useState<string>("");
  const [newSectionSummary, setNewSectionSummary] = useState<string>("");
  const [newSectionDetails, setNewSectionDetails] = useState<string>("");
  const [newSectionContent, setNewSectionContent] = useState<string>("");
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setConversation(null);
    setMessages([]);
    setInput("");
    setError(null);
    void loadConversation();
  }, [projectId]);

  const orderedMessages = useMemo(() => {
    return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages]);

  const loadConversation = async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchConversation(projectId);
      setConversation(result.conversation);
      setMessages(result.messages);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load conversation";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectId || !input.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      const result = await sendConversationMessage(projectId, input.trim());
      setConversation(result.conversation);
      setMessages(result.messages.length ? result.messages : [result.message]);
      setInput("");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send message";
      // eslint-disable-next-line no-console
      console.error("Conversation send failed", err);
      setError(message);
    } finally {
      setIsSending(false);
    }
  };

  const loadSpecSections = async () => {
    if (!projectId) return;

    setIsLoadingSpecSections(true);
    setSectionPickerError(null);

    try {
      const result = await fetchSpecSections(projectId);
      setSpecSections(result);
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Failed to load spec sections";
      setSectionPickerError(messageText);
    } finally {
      setIsLoadingSpecSections(false);
    }
  };

  const openSectionPicker = (message: ProjectConversationMessage) => {
    setMessageToPromote(message);
    setSelectedSpecSectionId("new");
    setNewSectionCategory("");
    setNewSectionKind("");
    setNewSectionTitle("");
    setNewSectionSummary("");
    setNewSectionDetails("");
    setNewSectionContent("");
    setIsSectionPickerOpen(true);
  };

  const closeSectionPicker = () => {
    setIsSectionPickerOpen(false);
    setMessageToPromote(null);
  };

  const handlePromote = async () => {
    if (!projectId || !messageToPromote) return;

    const targetSection = selectedSpecSectionId === "new"
      ? undefined
      : specSections.find((section) => section.id === selectedSpecSectionId);

    setPromotingId(messageToPromote.id);
    setPromotionNotice(null);
    setPromotionError(null);

    try {
      const result = await promoteConversationMessageToSpec(projectId, {
        projectId,
        messageId: messageToPromote.id,
        messageText: messageToPromote.content,
        specSectionId: targetSection?.id,
        targetCategory: targetSection?.category ?? (newSectionCategory || undefined),
        target: targetSection
          ? {
              category: targetSection.category ?? null,
              kind: targetSection.kind ?? null,
              summary: targetSection.summary ?? null,
              details: targetSection.details ?? null,
              content: targetSection.content ?? null,
              title: targetSection.title ?? null,
            }
          : {
              category: newSectionCategory || null,
              kind: newSectionKind || null,
              summary: newSectionSummary || null,
              details: newSectionDetails || null,
              content: newSectionContent || null,
              title: newSectionTitle || newSectionSummary || null,
            },
      });

      setPromotionNotice(
        `Added to ${result.specSource?.name ?? "project specifications"} as ${result.section.title}`,
      );
      setToastMessage("Spec section updated from conversation");
      onSpecUpdated?.();
      closeSectionPicker();
    } catch (err) {
      const messageText = err instanceof Error ? err.message : "Failed to promote message";
      setPromotionError(messageText);
    } finally {
      setPromotingId(null);
    }
  };

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [orderedMessages]);

  useEffect(() => {
    if (!isSectionPickerOpen) return;
    void loadSpecSections();
  }, [isSectionPickerOpen, projectId]);

  useEffect(() => {
    if (!toastMessage) return;
    const timeout = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(timeout);
  }, [toastMessage]);

  const renderSectionPicker = () => {
    if (!isSectionPickerOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6">
        <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-slate-800">Add message to specification</div>
              <div className="text-xs text-slate-600">Choose an existing section or create a new one.</div>
            </div>
            <button type="button" className="button button-ghost button-sm" onClick={closeSectionPicker}>
              Close
            </button>
          </div>
          <div className="space-y-3 px-4 py-3">
            {sectionPickerError ? <InlineAlert tone="danger" title={sectionPickerError} /> : null}
            <div className="rounded border border-slate-200">
              <label className="flex cursor-pointer items-start gap-3 border-b border-slate-200 px-3 py-2 last:border-b-0">
                <input
                  type="radio"
                  name="spec-section"
                  value="new"
                  checked={selectedSpecSectionId === "new"}
                  onChange={() => setSelectedSpecSectionId("new")}
                  className="mt-1"
                />
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-800">Create new section</span>
                  <span className="text-xs text-slate-600">
                    The assistant will generate a new section using this message.
                  </span>
                </div>
              </label>

              {selectedSpecSectionId === "new" ? (
                <div className="grid gap-3 border-t border-slate-200 px-3 py-3 md:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    <span>Category</span>
                    <input
                      className="input"
                      value={newSectionCategory}
                      onChange={(event: any) => setNewSectionCategory(event.target.value)}
                      placeholder="e.g. Functional"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    <span>Kind</span>
                    <input
                      className="input"
                      value={newSectionKind}
                      onChange={(event: any) => setNewSectionKind(event.target.value)}
                      placeholder="e.g. Feature"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    <span>Title</span>
                    <input
                      className="input"
                      value={newSectionTitle}
                      onChange={(event: any) => setNewSectionTitle(event.target.value)}
                      placeholder="Short section title"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-700">
                    <span>Summary</span>
                    <input
                      className="input"
                      value={newSectionSummary}
                      onChange={(event: any) => setNewSectionSummary(event.target.value)}
                      placeholder="1-2 sentence summary"
                    />
                  </label>
                  <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
                    <span>Details</span>
                    <textarea
                      className="input min-h-[120px]"
                      value={newSectionDetails}
                      onChange={(event: any) => setNewSectionDetails(event.target.value)}
                      placeholder="Additional details or acceptance criteria"
                    />
                  </label>
                  <label className="md:col-span-2 flex flex-col gap-1 text-sm text-slate-700">
                    <span>Optional content</span>
                    <textarea
                      className="input min-h-[120px]"
                      value={newSectionContent}
                      onChange={(event: any) => setNewSectionContent(event.target.value)}
                      placeholder="Provide content to include verbatim if needed"
                    />
                  </label>
                </div>
              ) : null}

              <div className="max-h-64 overflow-y-auto">
                {isLoadingSpecSections ? (
                  <p className="px-3 py-2 text-sm text-slate-600">Loading sections…</p>
                ) : specSections.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-slate-600">No existing sections found.</p>
                ) : (
                  specSections.map((section) => (
                    <label
                      key={section.id}
                      className="flex cursor-pointer items-start gap-3 border-t border-slate-200 px-3 py-2"
                    >
                      <input
                        type="radio"
                        name="spec-section"
                        value={section.id}
                        checked={selectedSpecSectionId === section.id}
                        onChange={() => setSelectedSpecSectionId(section.id)}
                        className="mt-1"
                      />
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                        {section.summary ? (
                          <span className="text-xs text-slate-600">{section.summary}</span>
                        ) : null}
                        {section.category ? (
                          <span className="text-[11px] uppercase tracking-wide text-slate-500">
                            {section.category}
                          </span>
                        ) : null}
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>

            {messageToPromote ? (
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Message preview</div>
                <div className="mt-1 whitespace-pre-line">{messageToPromote.content}</div>
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <button type="button" className="button button-ghost" onClick={closeSectionPicker}>
              Cancel
            </button>
            <button
              type="button"
              className="button button-primary"
              disabled={promotingId === messageToPromote?.id}
              onClick={() => void handlePromote()}
            >
              {promotingId === messageToPromote?.id ? "Adding…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-[70vh] flex-col gap-4">
      <PageHeader title="Conversation" />

      <div aria-live="polite">{error ? <InlineAlert tone="danger" title={error} /> : null}</div>
      <div aria-live="polite" className="space-y-2">
        {promotionNotice ? <InlineAlert tone="success" title={promotionNotice} /> : null}
        {promotionError ? <InlineAlert tone="danger" title={promotionError} /> : null}
      </div>

      <div className="flex h-full flex-1 flex-col rounded border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">Project assistant</div>
            <div className="text-xs text-slate-600">Conversational view powered by the project context.</div>
          </div>
          <div className="flex items-center gap-2">
            {isSending ? <span className="text-xs text-indigo-600">Assistant is thinking…</span> : null}
            <button
              type="button"
              className="button button-ghost"
              onClick={() => void loadConversation()}
              disabled={isLoading}
            >
              {isLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        <div ref={messageListRef} className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <p className="text-sm text-slate-600">Loading conversation…</p>
          ) : orderedMessages.length === 0 ? (
            <EmptyState
              title="No messages yet"
              description="Start the conversation to ask questions or request updates."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {orderedMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex flex-col gap-1 rounded border px-3 py-2 shadow-sm ${
                    message.role === "assistant" ? "border-indigo-100 bg-indigo-50/50" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {message.role}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(message.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="whitespace-pre-line text-sm text-slate-800">{message.content}</div>
                  {message.role === "assistant" ? (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <button
                        type="button"
                        className="button button-secondary button-xs"
                        onClick={() => openSectionPicker(message)}
                        disabled={promotingId === message.id}
                      >
                        {promotingId === message.id ? "Adding…" : "Add to spec"}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
          </div>
        )}
      </div>

        <form
          className="border-t border-slate-200 bg-slate-50 px-4 py-3"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => void handleSend(event)}
        >
          <label className="flex flex-col gap-2 text-sm text-slate-700">
            <span>Message</span>
            <textarea
              className="input min-h-[96px]"
              placeholder="Ask the assistant about the project"
              value={input}
              onChange={(event: any) => setInput(event.target.value)}
              disabled={isSending}
            />
          </label>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={isSending || !input.trim()}>
              {isSending ? "Sending…" : "Send"}
            </button>
            <span className="text-xs text-slate-500">Messages are saved per project with full context.</span>
          </div>
        </form>
      </div>
      {renderSectionPicker()}
      {toastMessage ? (
        <div className="fixed bottom-4 right-4 z-50 rounded border border-green-100 bg-white px-4 py-3 shadow-lg">
          <div className="text-sm font-semibold text-green-700">{toastMessage}</div>
        </div>
      ) : null}
    </div>
  );
}
